import type { Tables } from '../db/supabase.types.ts'
import type { TypedSupabaseClient } from '../db/types.ts'
import type { EventType } from './types.ts'

export type CreateEventPayload = {
  assetId: string
  title: string
  description?: string | null
  eventType: EventType
  eventDate?: Date | null
  userId?: string
}

export type UpdateEventPayload = {
  eventId: string
  title?: string
  description?: string | null
  eventType?: EventType
  eventDate?: Date | null
}

export async function createEvent(
  payload: CreateEventPayload,
  supabase: TypedSupabaseClient
): Promise<Tables<'events'> | null> {
  const { data, error } = await supabase
    .from('events')
    .insert({
      asset_id: payload.assetId,
      title: payload.title,
      description: payload.description ?? null,
      event_type: payload.eventType,
      event_date: payload.eventDate?.toISOString() ?? null,
      user_id: payload.userId,
    })
    .select()
    .single()

  if (error) {
    console.error('Error creating event:', error)
    return null
  }

  return data
}

export async function updateEvent(
  payload: UpdateEventPayload,
  supabase: TypedSupabaseClient
): Promise<Tables<'events'> | null> {
  const updateData: Record<string, string | null> = {}

  if (payload.title !== undefined) {
    updateData.title = payload.title
  }

  if (payload.description !== undefined) {
    updateData.description = payload.description
  }

  if (payload.eventType !== undefined) {
    updateData.event_type = payload.eventType
  }

  if (payload.eventDate !== undefined) {
    updateData.event_date = payload.eventDate?.toISOString() ?? null
  }

  const { data, error } = await supabase
    .from('events')
    .update(updateData)
    .eq('id', payload.eventId)
    .select()
    .single()

  if (error) {
    console.error('Error updating event:', error)
    return null
  }

  return data
}

export async function deleteEvent(
  eventId: string,
  supabase: TypedSupabaseClient
): Promise<boolean> {
  const { error } = await supabase.from('events').delete().eq('id', eventId)

  if (error) {
    console.error('Error deleting event:', error)
    return false
  }

  return true
}

export async function addContactToEvent(
  eventId: string,
  contactId: string,
  supabase: TypedSupabaseClient
): Promise<Tables<'event_contacts'> | null> {
  const { data, error } = await supabase
    .from('event_contacts')
    .insert({
      event_id: eventId,
      contact_id: contactId,
    })
    .select()
    .single()

  if (error) {
    console.error('Error adding contact to event:', error)
    return null
  }

  return data
}

export async function removeContactFromEvent(
  eventId: string,
  contactId: string,
  supabase: TypedSupabaseClient
): Promise<boolean> {
  const { error } = await supabase
    .from('event_contacts')
    .delete()
    .eq('event_id', eventId)
    .eq('contact_id', contactId)

  if (error) {
    console.error('Error removing contact from event:', error)
    return false
  }

  return true
}

export async function findEventByDateAndAsset(
  assetId: string,
  eventDate: Date,
  supabase: TypedSupabaseClient
): Promise<Tables<'events'> | null> {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('asset_id', assetId)
    .eq('event_date', eventDate.toISOString())
    .limit(1)
    .maybeSingle()

  if (error) {
    console.error('Error finding event by date and asset:', error)
    return null
  }

  return data
}
